import _ from "lodash";
import { chapter_cmp } from "@/utils/comperators";
import { chapter_normalize } from "./comperators";
import * as IDS from "@/utils/AuditItemListManipulatorIds";

const UNDEFINED_CHAPTER_VALUE = "999999.999999.999999.999999.999999.999999";

const FilterByManipulator = [
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.FILTER_IMPORTANCE,
    name: "Fragengewichtung",
    callback: (auditItem, filter) => {
      if (filter instanceof Array) {
        return filter.findIndex(v => v == auditItem.question.importance) >= 0;
      } else {
        return auditItem.question.importance == filter;
      }
    },
    aggregator: auditItem => {
      if (
        typeof auditItem.question.importance == "string" ||
        typeof auditItem.question.importance == "number"
      ) {
        return [
          {
            id: String(auditItem.question.importance),
            label: "Wert " + String(auditItem.question.importance)
          }
        ];
      } else {
        return null;
      }
    }
  }),
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.FILTER_CHAPTER,
    name: "Normkapitel",
    callback: (auditItem, filter) => {
      if (filter instanceof Array) {
        return (
          auditItem.question.chapters instanceof Array &&
          auditItem.question.chapters.findIndex(
            chapter => filter.findIndex(v => v == chapter.id) >= 0
          ) >= 0
        );
      } else {
        return (
          auditItem.question.chapters instanceof Array &&
          auditItem.question.chapters.findIndex(
            chapter => chapter.id == filter
          ) >= 0
        );
      }
    },
    aggregator: auditItem => {
      if (auditItem.question.chapters instanceof Array) {
        return auditItem.question.chapters.map(v => {
          return {
            id: v.id,
            label: v.id + " - " + v.description
          };
        });
      } else {
        return null;
      }
    }
  }),
  (Categories /*, FindingsOrder, FindingTypes*/) => ({
    id: IDS.FILTER_CATEGORY,
    name: "Kategorie",
    callback: (auditItem, filter) => {
      if (filter instanceof Array) {
        return filter.findIndex(v => v == auditItem.question.category) >= 0;
      } else {
        return auditItem.question.category == filter;
      }
    },
    aggregator: auditItem => {
      const category = extract_category(auditItem, Categories);
      if (category) {
        return [
          {
            label: category.v,
            id: category.k
          }
        ];
      } else {
        return null;
      }
    }
  }),
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.FILTER_ANSWERD,
    name: "Beantwortet",
    callback: (auditItem, filter) => {
      if (filter instanceof Array) {
        return (
          filter.findIndex(
            v => v == String(Object.keys(auditItem.deviations || {}).length > 0)
          ) >= 0
        );
      } else {
        return (
          String(Object.keys(auditItem.deviations || {}).length > 0) == filter
        );
      }
    },
    aggregator: auditItem => {
      const answerd = Object.keys(auditItem.deviations || {}).length > 0;
      return [
        {
          id: String(answerd),
          label: answerd ? "beantwortet" : "unbeantwortet"
        }
      ];
    }
  }),
  (Categories, FindingsOrder, FindingTypes) => ({
    id: IDS.FILTER_FINDING_IMPORTANCE,
    name: "Feststellung",
    callback: (auditItem, filter) => {
      if (filter instanceof Array) {
        if (auditItem.deviations) {
          for (const deviation of Object.values(auditItem.deviations)) {
            if (filter.findIndex(v => v == deviation.importance) >= 0) {
              return true;
            } else if (filter.findIndex(v => v == deviation.id) >= 0) {
              console.error("Unknown Finding Type", deviation.id);
              return true;
            }
          }
          return false;
        } else {
          return false;
        }
      } else {
        if (auditItem.deviations) {
          for (const deviation of Object.values(auditItem.deviations)) {
            if (deviation.importance == filter) {
              return true;
            } else if (deviation.id == filter) {
              console.error("Unknown Finding Type", deviation.id);
              return true;
            }
          }
          return false;
        } else {
          return false;
        }
      }
    },
    aggregator: auditItem => {
      const data = _.map(auditItem.deviations, val => {
        if (val.importance === undefined) {
          console.error("Unknown Finding Type", val, FindingTypes);
          if ("id" in val) {
            return {
              id: String(val.id),
              label: findingTypeId2Text(val.id, FindingTypes)
            };
          }
        }
        return {
          id: String(val.importance),
          label: findingTypeId2Text(val.importance, FindingTypes)
        };
      });
      return data;
    }
  })
];

const SortByManipulator = [
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.SORT_NO,
    name: "Nummer",
    cmp: (auditItemLhs, auditItemRhs) => {
      const lhs = chapter_normalize(
        auditItemLhs.question.no || UNDEFINED_CHAPTER_VALUE
      );
      const rhs = chapter_normalize(
        auditItemRhs.question.no || UNDEFINED_CHAPTER_VALUE
      );
      if (lhs < rhs) return -1;
      if (lhs > rhs) return 1;
      return 0;
    }
  }),
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.SORT_IMPORTANCE,
    name: "Fragengewichtung",
    cmp: (auditItemLhs, auditItemRhs) => {
      const lhs = parseInt(auditItemLhs.question.importance || 0, 10);
      const rhs = parseInt(auditItemRhs.question.importance || 0, 10);
      if (lhs < rhs) return -1;
      if (lhs > rhs) return 1;
      return 0;
    }
  }),
  (Categories, FindingsOrder /*, FindingTypes*/) => ({
    id: IDS.SORT_FINDING_IMPORTANCE,
    name: "Feststellung",
    cmp: (auditItemLhs, auditItemRhs) => {
      const lhs = extractFindingOrder(auditItemLhs.deviations, FindingsOrder);
      const rhs = extractFindingOrder(auditItemRhs.deviations, FindingsOrder);
      return lhs - rhs;
    }
  }),
  (Categories /*, FindingsOrder, FindingTypes*/) => ({
    id: IDS.SORT_CATEGORY,
    name: "Kategorie",
    cmp: (auditItemLhs, auditItemRhs) => {
      const lhs = extract_category(auditItemLhs, Categories) || "";
      const rhs = extract_category(auditItemRhs, Categories) || "";

      if (lhs.v < rhs.v) return -1;
      if (lhs.v > rhs.v) return 1;
      return 0;
    }
  }),
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.SORT_CHAPTER,
    name: "Normkapitel",
    cmp: (auditItemLhs, auditItemRhs) => {
      return chapter_cmp(
        extract_chapter_id(auditItemLhs),
        extract_chapter_id(auditItemRhs)
      );
    }
  })
];
const GroupByManipulator = [
  (Categories /*, FindingsOrder, FindingTypes */) => ({
    id: IDS.GROUP_CATEGORY,
    name: "Kategorie",
    group_infos: auditItem => {
      const category = extract_category(auditItem, Categories);
      if (category) {
        return {
          title: category.v,
          id: category.k
        };
      } else {
        return {
          title: "Kategorie: Undefiniert",
          id: "zzzzzzzzzzzzzzzzzzzzzzzzz-random0902300812309kjsadlkjadslkj"
        };
      }
    }
  }),
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.GROUP_IMPORTANCE,
    name: "Fragengewichtung",
    group_infos: auditItem => {
      return {
        title: `Fragengewichtung: ${auditItem.question.importance}`,
        id: parseInt(auditItem.question.importance || 0, 10)
      };
    }
  }),
  (/*Categories, FindingsOrder, FindingTypes*/) => ({
    id: IDS.GROUP_CHAPTER,
    name: "Normkapitel",
    group_infos: auditItem => {
      const chapter_id = extract_chapter_id(auditItem);
      if (chapter_id !== UNDEFINED_CHAPTER_VALUE) {
        const description = auditItem.question.chapters[0].description;
        return {
          title: `${chapter_id} - ${description}`,
          id: chapter_normalize(chapter_id)
        };
      } else {
        return {
          title: "Undefiniert",
          id: UNDEFINED_CHAPTER_VALUE
        };
      }
    }
  })
];

const CreateAuditItemListManipulators = (
  Categories,
  FindingsOrder,
  FindingTypes
) => {
  return {
    filter_by: FilterByManipulator.map(construct =>
      construct(Categories, FindingsOrder, FindingTypes)
    ),
    sort_by: SortByManipulator.map(construct =>
      construct(Categories, FindingsOrder, FindingTypes)
    ),

    grouped_by: GroupByManipulator.map(construct =>
      construct(Categories, FindingsOrder, FindingTypes)
    )
  };
};

export default CreateAuditItemListManipulators;

function findingTypeId2Text(Id, FindingTypes) {
  Id = String(Id);
  const FindingType = FindingTypes.find(val => val.id === Id);
  if (FindingType) {
    return FindingType.text;
  } else {
    return Id;
  }
}
function extract_chapter_id(auditItem) {
  if (
    auditItem.question.chapters &&
    auditItem.question.chapters.length > 0 &&
    auditItem.question.chapters[0].id
  ) {
    return String(auditItem.question.chapters[0].id);
  } else {
    return UNDEFINED_CHAPTER_VALUE;
  }
}

function extract_category(auditItem, categories) {
  const category = auditItem.question.category;
  if (Object.keys(categories).length) {
    if (typeof category === "string" && categories[category]) {
      return { k: category, v: categories[category].name };
    } else if (
      category instanceof Array &&
      category.length > 0 &&
      categories[category[0]]
    ) {
      return { k: category[0], v: categories[category[0]].name };
    } else {
      return null;
    }
  } else {
    if (typeof category === "string") {
      return { k: category, v: category };
    } else if (category instanceof Array && category.length > 0) {
      return { k: category[0], v: category[0] };
    } else {
      return null;
    }
  }
}

function extractFindingOrder(Deviations, FindingsOrder) {
  if (Deviations) {
    let importance = FindingsOrder.length;
    for (const [key, deviation] of Object.entries(Deviations)) {
      key;
      importance = Math.min(
        importance,
        importanceId2Order(deviation.importance, FindingsOrder)
      );
    }
    return importance;
  } else {
    return importanceId2Order(null, FindingsOrder);
  }
}

function importanceId2Order(Id, FindingsOrder) {
  return FindingsOrder.findIndex(v => v == Id);
}
